// useFocusOnRoute — TanStack Router subscriber that focuses the page <h1>
// (or <main id="main"> as a fallback) on every navigation.
//
// Plan ref: 06-02-PLAN §Task 1 step 5
//          + 06-RESEARCH-motion-responsive-a11y.md §10 (focus management)
//          + WCAG 2.2 AA — 2.4.3 Focus Order
//
// Behavior:
//   - Subscribes to router.subscribe('onResolved', ...) so we focus AFTER the
//     destination route mounts (route-load chains fire onResolved last).
//   - Defers one rAF before focusing so React has flushed the new tree's
//     refs and the h1 is in the DOM.
//   - Adds tabIndex=-1 to the focus target on the fly so programmatic focus
//     succeeds without forcing every lane to author a tabIndex prop. Tab
//     navigation will still skip the h1 (only focusable elements with
//     positive or 0 tabIndex are in the natural tab order).
//   - Falls back to <main id="main"> when no <h1> is present (e.g. lanes
//     that haven't yet authored a heading).

import { useEffect } from 'react';

interface Routerlike {
  subscribe(event: 'onResolved', cb: () => void): () => void;
}

export function useFocusOnRoute(router: Routerlike): void {
  useEffect(() => {
    const unsubscribe = router.subscribe('onResolved', () => {
      // Defer one frame so the destination tree has mounted before we look
      // up the heading.
      requestAnimationFrame(() => {
        const target =
          (document.querySelector('main h1') as HTMLElement | null) ??
          (document.querySelector('h1') as HTMLElement | null) ??
          (document.getElementById('main') as HTMLElement | null);
        if (!target) return;
        // Make sure the element can receive programmatic focus without
        // entering the tab order — tabIndex=-1 is the canonical pattern.
        if (target.tabIndex !== -1 && target.getAttribute('tabindex') == null) {
          target.tabIndex = -1;
        }
        target.focus({ preventScroll: true });
      });
    });
    return unsubscribe;
  }, [router]);
}
